import React, { useState, useEffect } from 'react';
import api from '../utils/api';

const COLORS = {
  on_target:    { bg: '#ECFDF5', text: '#065F46', accent: '#10B981' },
  below_target: { bg: '#FFFBEB', text: '#92400E', accent: '#F59E0B' },
  major_leak:   { bg: '#FEF2F2', text: '#991B1B', accent: '#EF4444' },
  untracked:    { bg: '#F9FAFB', text: '#6B7280', accent: '#D1D5DB' },
};

const stageStatus = (s) => {
  if (!s.tracked) return 'untracked';
  if (s.actual >= s.target) return 'on_target';
  if (s.actual >= s.target * 0.7) return 'below_target';
  return 'major_leak';
};

// Phase 3: USD formatters for dashboard.
// Compact format for KPI cards and per-stage rows where big numbers
// would crowd the layout. Matches HubSpot's "$834.33K" pattern.
const formatUSDCompact = (n) => {
  if (n === null || n === undefined || n === '' || Number.isNaN(Number(n))) return '—';
  const num = Number(n);
  if (num === 0) return '$0';
  const abs = Math.abs(num);
  if (abs >= 1_000_000) {
    return `$${(num / 1_000_000).toFixed(2).replace(/\.?0+$/, '')}M`;
  }
  if (abs >= 1_000) {
    return `$${(num / 1_000).toFixed(2).replace(/\.?0+$/, '')}K`;
  }
  return `$${num.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

const formatAvgPerDeal = (total, count) => {
  if (!count || count === 0) return null;
  return `Avg per deal ${formatUSDCompact(total / count)}`;
};

const FunnelView = ({ from, to }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api.get(`/stats/funnel?from=${from}&to=${to}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.error || 'Failed to load funnel'))
      .finally(() => setLoading(false));
  }, [from, to]);

  if (loading) return <div style={{ padding: 24, color: '#6B7280' }}>Loading funnel…</div>;
  if (error) return <div style={{ padding: 24, color: '#EF4444' }}>{error}</div>;
  if (!data) return null;

  const { stages, submetrics, conversion_rates, pipeline, kpis } = data;
  const closed = stages.find(s => s.key === 'closed_won');

  // Conversion text between adjacent stages
  const conv = [
    { label: `${submetrics.accept_rate}% accept · ${submetrics.reply_rate_from_accepted}% reply (sub-metric)`, leak: false },
    { label: `${conversion_rates.replies_to_qualified}% qualify`, leak: stages[1].actual > 0 && conversion_rates.replies_to_qualified < 5 },
    { label: stages[2].actual > 0 ? `${conversion_rates.qualified_to_discovery}% to discovery` : 'insufficient data', leak: false },
    { label: stages[3].actual > 0 ? `${conversion_rates.discovery_to_proposal}% to proposal` : 'insufficient data', leak: false },
    { label: stages[4].actual > 0 ? `${conversion_rates.proposal_to_negotiation}% to negotiation` : 'insufficient data', leak: false },
    { label: stages[5].actual > 0 ? `${conversion_rates.negotiation_to_close}% close rate` : 'insufficient data', leak: false },
  ];

  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>
        Sales Funnel — {data.period.from} to {data.period.to}
      </div>

      {/* Phase 3: Deal value KPI cards (HubSpot-equivalent dashboard). */}
      {kpis && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 12 }}>
          <Card
            label="Total Deal Amount"
            value={formatUSDCompact(kpis.total_value)}
            subtext={formatAvgPerDeal(kpis.total_value, kpis.total_count)}
          />
          <Card
            label="Weighted Deal Amount"
            value={formatUSDCompact(kpis.weighted_value)}
            subtext="Pipeline × stage probability"
          />
          <Card
            label="Open Deal Amount"
            value={formatUSDCompact(kpis.open_value)}
            subtext={formatAvgPerDeal(kpis.open_value, kpis.open_count)}
          />
          <Card
            label="Closed Deal Amount"
            value={formatUSDCompact(kpis.closed_value)}
            subtext={`${kpis.closed_count} won · this period`}
            color="#10B981"
          />
          <Card
            label="Average Deal Age"
            value={kpis.avg_deal_age_days > 0 ? `${kpis.avg_deal_age_days} d` : '—'}
            subtext="Open leads only"
          />
        </div>
      )}

      {/* Existing summary cards row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
        <Card label="Closed Won" value={`${closed.actual} / ${closed.target}`} color={closed.actual >= closed.target ? '#10B981' : '#EF4444'} />
        <Card label="Reply → Hot" value={`${conversion_rates.replies_to_qualified}%`} color={conversion_rates.replies_to_qualified >= 5 ? '#10B981' : '#EF4444'} />
        <Card label="Active Leads" value={pipeline.active_leads} />
        <Card label="Period" value={`${data.period.days} days`} />
      </div>

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: 8, padding: 16 }}>
        {stages.map((s, i) => {
          const status = stageStatus(s);
          const c = COLORS[status];
          const hasValue = s.value_total !== undefined && s.value_total > 0;
          return (
            <React.Fragment key={s.key}>
              <div style={{ display: 'grid', gridTemplateColumns: '18px minmax(0, 1fr) auto', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 6, background: c.bg, borderLeft: `3px solid ${c.accent}`, marginBottom: 4 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: '#9CA3AF' }}>{i + 1}</div>
                <div style={{ fontSize: 14, fontWeight: 500, color: c.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label}</div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 16, fontWeight: 500, color: status === 'major_leak' ? '#EF4444' : '#111827' }}>
                    {s.tracked ? s.actual : '—'}
                  </div>
                  <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                    target {s.target}{!s.tracked && ' · not tracked'}
                  </div>
                  {hasValue && (
                    <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                      {formatUSDCompact(s.value_total)} total · {formatUSDCompact(s.value_weighted)} weighted
                    </div>
                  )}
                </div>
              </div>
              {i < stages.length - 1 && (
                <div style={{ paddingLeft: 38, padding: '2px 0 2px 38px', fontSize: 11, color: conv[i]?.leak ? '#EF4444' : '#9CA3AF', fontWeight: conv[i]?.leak ? 500 : 400 }}>
                  ↓ {conv[i]?.label || 'insufficient data'}{conv[i]?.leak ? ' · primary leak — should be 6%+' : ''}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 11, color: '#6B7280' }}>
        {Object.entries(COLORS).map(([k, v]) => (
          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: v.accent }} />
            {k.replace('_', ' ')}
          </div>
        ))}
      </div>
    </div>
  );
};

const Card = ({ label, value, color, subtext }) => (
  <div style={{ background: '#F9FAFB', borderRadius: 8, padding: 16 }}>
    <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 6 }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 500, color: color || '#111827' }}>{value}</div>
    {subtext && (
      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>{subtext}</div>
    )}
  </div>
);

export default FunnelView;